import React from "react";
import { useTranslation } from "react-i18next";
import { CrownIcon } from "../icons/CrownIcon";
import ImageNotFound from "../../images/imagenotfound.jpg";
import { MapPinIcon } from "@heroicons/react/24/solid";
import { formatPhoneNumber } from "../../globalFunctions/PhoneNumberFormatter";

const ServiceCard = ({
  service,
  openEditModal,
  priceTypesList,
  showAddToCartButton = false,
  onAddToCart,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];

  let status = "";
  if (!service) service = null;
  if (service?.visibility?.hidden) status = t("status.hidden");
  else if (service?.visibility?.published) status = t("status.published");

  const priceSuffix = (() => {
    const found = priceTypesList?.find(
      (type) => type.id == service?.priceTypeId
    );
    return found && found.id !== 0 && found.id !== 5
      ? ` / ${lang === "en" ? found.nameEn : found.nameFr}`
      : "";
  })();

  return (
    <div
      className="shadow-lg rounded-lg overflow-hidden bg-white flex flex-col transition hover:shadow-xl min-h-[400px] max-h-[400px] hover:cursor-pointer hover:scale-105"
      onClick={openEditModal}
    >
      <div className="relative h-48">
        <img
          src={service.image_url || service.imageUrl || ImageNotFound}
          alt={service.name}
          loading="lazy"
          className="w-full h-48 object-cover"
        />
        <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 via-black/50 p-4 flex justify-between items-center">
          <p className="text-gray-100 font-bold text-xl mt-4">
            ${service.price}
            {priceSuffix}
          </p>
          <CrownIcon className="text-yellow-400 w-7 h-7 mt-4" />
        </div>
      </div>

      <div className="flex flex-col p-4 gap-1 flex-grow">
        <h2 className="text-lg font-semibold text-gray-800 line-clamp-1">
          {lang === "fr" ? service?.nameFr : service?.nameEn}
        </h2>
        <p className="text-sm text-gray-600">
          {t("categories." + service?.category)} {status}
        </p>
        <p className="text-gray-500 text-sm line-clamp-3">
          {lang === "fr" ? service?.descriptionFr : service?.descriptionEn}
        </p>

        {service?.showAddress && service?.location?.formattedAddress && (
          <div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                service.location.formattedAddress
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-secondary hover:underline mt-2"
            >
              <MapPinIcon className="w-5 h-5 text-muted" />
              {service.location.formattedAddress}
            </a>
          </div>
        )}

        {service?.tags?.length > 0 && (
          <div className="flex flex-wrap text-primary">
            {service.tags.map((tag, index) => (
              <span
                key={index}
                className="text-gray-500 bg-gray-200 text-sm px-2 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {service?.serviceInventoryLimits?.inventoryMode === "contact" ? (
          <div className="mt-auto pt-4 text-center">
            <p className="text-secondary font-medium text-sm">
              {t("global.contact_us_to_book")} :{" "}
              <span className="font-semibold text-primary">
                {formatPhoneNumber(service?.company?.phoneNumber) ??
                  t("global.no_phone_available")}
              </span>
            </p>
          </div>
        ) : showAddToCartButton ? (
          <div className="mt-auto pt-4 text-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart();
              }}
              className="w-full bg-primary text-white px-4 py-2 rounded-full hover:bg-primary/90 transition"
            >
              {t("global.add_to_cart")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ServiceCard;
